import { Pipe, PipeTransform } from '@angular/core';
import { Duration, toDuration } from '../interfaces/duration';
import { TranslocoService } from '@jsverse/transloco';

interface DurationPipeOptions {
  format?: string;
  separator?: string;
  useFullNames?: boolean;
  isSourceInMinutes?: boolean;
}

@Pipe({
  name: 'duration',
  standalone: true
})
export class DurationPipe implements PipeTransform {
  constructor(private translocoService: TranslocoService) { }

  transform(value: string | Duration | number, options: DurationPipeOptions | string = {}): string {
    // Handle case when options is just a format string
    if (typeof options === 'string') {
      options = { format: options };
    }

    const {
      format = 'HH:mm:ss',
      separator = ' ',
      useFullNames = false,
      isSourceInMinutes = false
    } = options;

    const duration = toDuration(value, isSourceInMinutes);

    // If no format is provided, return empty string
    if (!format) {
      return '';
    }

    // Parse the format string to determine which units to display
    const showDays = format.includes('DD');
    const showHours = format.includes('HH') || format.includes('H');
    const showMinutes = format.includes('mm') || format.includes('m');
    const showSeconds = format.includes('ss') || format.includes('s');
    const showMilliseconds = format.includes('SSS') || format.includes('S');

    // Determine if we should use zero-padding based on format
    const padHours = format.includes('HH');
    const padMinutes = format.includes('mm');
    const padSeconds = format.includes('ss');
    const padMilliseconds = format.includes('SSS');

    // For custom formatting
    if (format.includes(':') || format.includes('-') || format.includes('/')) {
      return this.formatWithPattern(duration, format, {
        showDays, showHours, showMinutes, showSeconds, showMilliseconds,
        padHours, padMinutes, padSeconds, padMilliseconds
      });
    }

    // For text-based formatting (using the original implementation)
    let remainingMillis = duration.toMillis();
    const parts: string[] = [];

    if (showDays && remainingMillis >= 86400000) {
      const days = Math.floor(remainingMillis / 86400000);
      parts.push(this.formatPart('day', days, useFullNames));
      remainingMillis -= days * 86400000;
    }

    if (showHours && remainingMillis >= 3600000) {
      const hours = Math.floor(remainingMillis / 3600000);
      parts.push(this.formatPart('hour', hours, useFullNames));
      remainingMillis -= hours * 3600000;
    }

    if (showMinutes && remainingMillis >= 60000) {
      const minutes = Math.floor(remainingMillis / 60000);
      parts.push(this.formatPart('minute', minutes, useFullNames));
      remainingMillis -= minutes * 60000;
    }

    if (showSeconds && remainingMillis >= 1000) {
      const seconds = Math.floor(remainingMillis / 1000);
      parts.push(this.formatPart('second', seconds, useFullNames));
      remainingMillis -= seconds * 1000;
    }

    if (showMilliseconds && remainingMillis > 0) {
      parts.push(this.formatPart('millisecond', remainingMillis, useFullNames));
    }

    return parts.join(separator);
  }

  private formatWithPattern(
    duration: Duration,
    format: string,
    options: {
      showDays: boolean,
      showHours: boolean,
      showMinutes: boolean,
      showSeconds: boolean,
      showMilliseconds: boolean,
      padHours: boolean,
      padMinutes: boolean,
      padSeconds: boolean,
      padMilliseconds: boolean
    }
  ): string {
    let result = format;
    const totalMillis = duration.toMillis();

    const days = Math.floor(totalMillis / 86400000);
    const hours = Math.floor((totalMillis % 86400000) / 3600000);
    const minutes = Math.floor((totalMillis % 3600000) / 60000);
    const seconds = Math.floor((totalMillis % 60000) / 1000);
    const milliseconds = totalMillis % 1000;

    // Replace format tokens with actual values
    if (options.showDays) {
      result = result.replace(/DD/g, days.toString().padStart(2, '0'));
      result = result.replace(/D/g, days.toString());
    }

    if (options.showHours) {
      const hoursValue = options.padHours ? hours.toString().padStart(2, '0') : hours.toString();
      result = result.replace(/HH/g, hoursValue);
      result = result.replace(/H/g, hours.toString());
    }

    if (options.showMinutes) {
      const minutesValue = options.padMinutes ? minutes.toString().padStart(2, '0') : minutes.toString();
      result = result.replace(/mm/g, minutesValue);
      result = result.replace(/m/g, minutes.toString());
    }

    if (options.showSeconds) {
      const secondsValue = options.padSeconds ? seconds.toString().padStart(2, '0') : seconds.toString();
      result = result.replace(/ss/g, secondsValue);
      result = result.replace(/s/g, seconds.toString());
    }

    if (options.showMilliseconds) {
      const msValue = options.padMilliseconds ? milliseconds.toString().padStart(3, '0') : milliseconds.toString();
      result = result.replace(/SSS/g, msValue);
      result = result.replace(/S/g, milliseconds.toString());
    }

    return result;
  }

  private formatPart(unit: string, value: number, useFullName: boolean): string {
    const translationKey = useFullName ? `duration.${unit}` : `duration.${unit}_short`;
    const translatedUnit = this.translocoService.translate(translationKey, { count: value });
    return `${value}${useFullName ? ' ' : ''}${translatedUnit}`;
  }
}
